/**
 * Pure mappers from the Scoping AI-generation responses to table state (Stage 2 Scoping,
 * PolicyLense Workflow slide 6). User ruling: stubs / invented data are not allowed; absent is
 * acceptable, fake is not. The backend now returns nulls / blanks / failed rows instead of
 * plausible defaults, so none of these helpers may turn an absent value into 0, a default role,
 * or a made-up label.
 */
import { isBlankMoney } from "./pricingCalculations";

/** Shown on a RACI row the AI could not draft. The row's R/A/C/I cells are left as the user had them. */
export const RACI_AI_FAILED_LABEL = "AI could not assign: fill in manually";

/**
 * Row-level "the AI failed on this row" marker. It is a property on the row ARRAY itself so it
 * is dropped by every immutable edit (`row.map(...)` / `[...row]`), i.e. the moment the user
 * touches the row, and never reaches JSON persistence (JSON.stringify ignores array props).
 */
export function markRowAiFailed(row, reason) {
  const next = [...row];
  next.aiFailed = true;
  next.aiFailureReason = reason || "";
  return next;
}
export function isRowAiFailed(row) {
  return !!row && row.aiFailed === true;
}

const colIndex = (header, needle) => (header || []).findIndex((h) => String(h || "").toLowerCase().includes(needle));

/**
 * Merge RACI assignments into the table rows (1:1 by index).
 * - a normal row gets its four cells from the AI (blank stays blank);
 * - an `aiFailed` row keeps whatever the user already had and is flagged for manual fill-in.
 * @returns {{ rows: string[][], failedCount: number }}
 */
export function applyRaciAssignments(prevRows, assignments, header) {
  const respIdx = colIndex(header, "responsible");
  const accIdx = colIndex(header, "accountable");
  const conIdx = colIndex(header, "consulted");
  const infIdx = colIndex(header, "informed");
  let failedCount = 0;
  const rows = (prevRows || []).map((row, i) => {
    const ai = (assignments || [])[i];
    if (!ai) return row;
    if (ai.aiFailed === true) {
      failedCount += 1;
      return markRowAiFailed(row, ai.aiFailureReason);
    }
    const updated = [...row];
    if (respIdx >= 0) updated[respIdx] = ai.responsible || "";
    if (accIdx >= 0) updated[accIdx] = ai.accountable || "";
    if (conIdx >= 0) updated[conIdx] = ai.consulted || "";
    if (infIdx >= 0) updated[infIdx] = ai.informed || "";
    return updated;
  });
  return { rows, failedCount };
}

/**
 * Merge resource-effort assignments. `d` is (number|null)[]; null (or any non-numeric) is an
 * EMPTY cell, never "0". A row whose `d` is not an array is left untouched.
 */
export function applyEffortAssignments(prevRows, assignments, effortCols) {
  return (prevRows || []).map((row, i) => {
    const ai = (assignments || [])[i];
    if (!ai || !Array.isArray(ai.d)) return row;
    const next = [...row];
    effortCols.forEach(({ ci }, j) => {
      const v = ai.d[j];
      const n = v === null || v === undefined || v === "" ? NaN : Math.round(Number(v));
      next[ci] = Number.isFinite(n) ? String(Math.max(0, n)) : "";
    });
    return next;
  });
}

/**
 * Notice lines for a generate response: the server's own `warnings` first; when a failure count
 * is reported without any warning text, a plain sentence so a failure is never silent.
 */
export function generationNoticeMessages(data, noun) {
  const out = Array.isArray(data?.warnings) ? data.warnings.filter((w) => typeof w === "string" && w.trim()) : [];
  const failed = Number(data?.failedRowCount) || 0;
  if (failed > 0 && out.length === 0) {
    out.push(`${failed} ${noun} row${failed === 1 ? "" : "s"} could not be drafted by AI and were left blank. Fill them in manually or retry.`);
  }
  return out;
}

const PRICING_WARNING_TEXT = {
  PRICING_UNPRICED_LINES: "Some lines have no rate or quantity, so no price was drafted for them. Enter a rate for each line marked 'Rate needed'.",
  PRICING_TRANCHES_NOT_100_PERCENT: "The payment tranche percentages do not add up to 100%. Correct them before submitting.",
};
export function describePricingWarning(code) {
  const c = String(code || "");
  if (c.startsWith("PRICING_RATE_NOT_IN_INPUT:")) {
    const head = c.slice("PRICING_RATE_NOT_IN_INPUT:".length) || "a line";
    return `The AI proposed a rate for '${head}' that was not in your inputs, so it was discarded. Enter the rate yourself.`;
  }
  return PRICING_WARNING_TEXT[c] || c;
}

/**
 * Map the pricing/generate payload to table state. Blank unit/total prices and blank tranche
 * shares/amounts stay '' (never "0", never "0%"). `fmt` formats a present amount for storage.
 */
export function mapPricingResponse(data, ts, fmt) {
  const money = (v) => (isBlankMoney(v) ? "" : fmt(v));
  const priceEstimate = (data?.priceEstimate || []).map((pe, i) => ({
    id: `pe-${ts}-${i}`,
    srNo: String(pe.srNo || i + 1),
    expenseHead: pe.expenseHead || "",
    unitOfMeasurement: pe.unitOfMeasurement || "",
    totalUnits: pe.totalUnits != null ? String(pe.totalUnits) : "",
    unitPrice: money(pe.unitPrice),
    totalPrice: money(pe.totalPrice),
    isHeader: true,
    children: (pe.children || []).map((c, j) => ({
      id: `pe-${ts}-${i}-c-${j}`,
      srNo: "",
      expenseHead: c.expenseHead || "",
      unitOfMeasurement: c.unitOfMeasurement || "",
      totalUnits: c.totalUnits != null ? String(c.totalUnits) : "",
      unitPrice: money(c.unitPrice),
      totalPrice: money(c.totalPrice),
    })),
  }));
  const paymentTranches = (data?.paymentTranches || []).map((t, i) => ({
    id: `pt-${ts}-${i}`,
    srNo: "",
    particulars: t.particulars || `Tranche ${i + 1}`,
    description: t.description || "",
    pctOfTotal: isBlankMoney(t.pctOfTotal) ? "" : String(t.pctOfTotal),
    payableAmount: money(t.payableAmount),
  }));
  const warnings = (Array.isArray(data?.warnings) ? data.warnings : []).map(describePricingWarning);
  return { priceEstimate, paymentTranches, warnings };
}

/**
 * What to show for an analyze-controls result. `count_only` (or any unknown basis) shows ONLY the
 * count: no control list, never labelled "applicable". `requirementsCovered` is not a measure
 * (the server always returns 0) and is never surfaced.
 */
export function describeControlAnalysis(data) {
  if (!data || typeof data !== "object") return null;
  const count = Number.isFinite(Number(data.controlsSatisfied)) ? Number(data.controlsSatisfied) : null;
  const validated =
    data.applicableControlsBasis === "ai_cited_validated" &&
    Array.isArray(data.applicableControls) &&
    data.applicableControls.length > 0;
  const rejected = Array.isArray(data.rejectedControlNumbers) ? data.rejectedControlNumbers.filter(Boolean).map(String) : [];
  return {
    basis: validated ? "ai_cited_validated" : "count_only",
    count,
    controls: validated ? data.applicableControls : [],
    rejectedControlNumbers: rejected,
  };
}

const FIELD_LABELS = {
  legalName: "legal name",
  website: "website",
  linkedin: "LinkedIn",
  contactName: "contact name",
  contactNumber: "contact number",
  contactEmail: "contact email",
  billingAddress: "billing address",
};
/** 'Not found with a source: a, b' for the company-profile AI response, or null when nothing was rejected. */
export function describeRejectedFields(rejectedFields) {
  const names = (Array.isArray(rejectedFields) ? rejectedFields : [])
    .map((r) => (typeof r === "string" ? r : r?.field))
    .filter(Boolean)
    .map((f) => FIELD_LABELS[f] || String(f).replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase());
  if (names.length === 0) return null;
  return `Not found with a source: ${names.join(", ")}. Fill these in yourself.`;
}
